import React, { useState } from 'react';
import { bookingsAPI } from './services/api';
import './ReservationLookup.css';

interface BookingDetails {
  reservation_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  product_type: string;
  booking_date: string;
  total_price: number;
  status: string;
  payment_status: string;
  items?: {
    roomTypeName?: string;
    roomTypeId?: string;
    sunbeds?: any[];
    sunbedCount?: number;
    guestCount?: number;
    totalPersons?: number;
    zones?: any[];
    zoneCount?: number;
  };
  customer_info?: {
    checkIn?: string;
    checkOut?: string;
    adults?: number;
    children?: number;
    guests?: number;
    phone?: string;
    arrivalTime?: string;
    zoneDetails?: any[];
  };
}

interface ReservationLookupProps {
  onBack: () => void;
}

const ReservationLookup: React.FC<ReservationLookupProps> = ({ onBack }) => {
  const [searchMode, setSearchMode] = useState<'reservation' | 'email'>('email');
  const [reservationId, setReservationId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState<BookingDetails[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<BookingDetails | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (searchMode === 'reservation') {
      if (!reservationId.trim() || !email.trim()) {
        setError('Lütfen rezervasyon numarası ve e-posta adresinizi girin');
        return;
      }
    } else {
      if (!email.trim() || !phone.trim()) {
        setError('Lütfen e-posta adresinizi ve telefon numaranızı girin');
        return;
      }
    }

    setLoading(true);
    setError('');
    setSearched(true);
    setBookings([]);
    setSelectedBooking(null);

    try {
      if (searchMode === 'reservation') {
        const response = await bookingsAPI.lookupReservation(reservationId.trim(), email.trim());
        if (response.data.success && response.data.data) {
          setBookings([response.data.data]);
        } else {
          setError('Rezervasyon bulunamadı.');
        }
      } else {
        const response = await bookingsAPI.lookupByEmail(email.trim(), phone.trim());
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          setBookings(response.data.data);
        } else {
          setError('Bu bilgilerle kayıtlı rezervasyon bulunamadı.');
        }
      }
    } catch (err: any) {
      console.error('Rezervasyon sorgu hatası:', err);
      if (err.response?.status === 404) {
        setError('Rezervasyon bulunamadı. Lütfen bilgileri kontrol edin.');
      } else if (err.response?.status === 403) {
        setError('E-posta ve telefon numarası eşleşmiyor.');
      } else {
        setError('Sorgulama sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string } } = {
      'pending_payment': { text: 'Ödeme Bekleniyor', color: '#f39c12' },
      'paid': { text: 'Ödendi', color: '#27ae60' },
      'confirmed': { text: 'Onaylandı', color: '#27ae60' },
      'paid_pending_confirmation': { text: 'Ödendi - Onay Bekliyor', color: '#3498db' },
      'cancelled': { text: 'İptal Edildi', color: '#e74c3c' },
      'completed': { text: 'Tamamlandı', color: '#95a5a6' },
    };
    return statusMap[status] || { text: status, color: '#7f8c8d' };
  };

  const calculateNights = (booking: BookingDetails) => {
    if (!booking?.customer_info?.checkIn || !booking?.customer_info?.checkOut) return 0;
    const checkIn = new Date(booking.customer_info.checkIn);
    const checkOut = new Date(booking.customer_info.checkOut);
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getProductTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      room: '🏨 Oda Rezervasyonu',
      sunbed: '🏖️ Şezlong Kiralama',
      breakfast: '🍳 Kahvaltı Rezervasyonu',
      dinner: '🍽️ Akşam Yemeği Rezervasyonu'
    };
    return labels[type] || type;
  };

  const getProductIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      room: '🏨',
      sunbed: '🏖️',
      breakfast: '🍳',
      dinner: '🍽️'
    };
    return icons[type] || '📋';
  };

  const resetSearch = () => {
    setBookings([]);
    setSelectedBooking(null);
    setSearched(false);
    setError('');
  };

  return (
    <div className="reservation-lookup-page">
      <div className="lookup-container">
        <button className="btn-back" onClick={onBack}>
          ← Ana Sayfa
        </button>

        <div className="lookup-header">
          <h1>🔍 Rezervasyonumu Sorgula</h1>
          <p>Rezervasyonlarınızı görüntülemek için e-posta ve telefon bilgilerinizi girin.</p>
        </div>

        {!selectedBooking && bookings.length === 0 && (
          <>
            <div className="search-mode-tabs">
              <button 
                className={`tab ${searchMode === 'email' ? 'active' : ''}`}
                onClick={() => { setSearchMode('email'); setError(''); }}
              >
                📧 E-posta + Telefon ile Ara
              </button>
              <button 
                className={`tab ${searchMode === 'reservation' ? 'active' : ''}`}
                onClick={() => { setSearchMode('reservation'); setError(''); }}
              >
                🔢 Rezervasyon No ile Ara
              </button>
            </div>

            <form onSubmit={handleSearch} className="lookup-form">
              {searchMode === 'reservation' && (
                <div className="form-group">
                  <label>Rezervasyon Numarası</label>
                  <input
                    type="text"
                    value={reservationId}
                    onChange={(e) => setReservationId(e.target.value)}
                    placeholder="Örn: RV-26-000001"
                  />
                </div>
              )}

              <div className="form-group">
                <label>E-posta Adresi</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Rezervasyon sırasında kullandığınız e-posta"
                  required
                />
              </div>

              {searchMode === 'email' && (
                <div className="form-group">
                  <label>Telefon Numarası</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Örn: 0532 123 45 67"
                    required
                  />
                  <small className="field-hint">Rezervasyon sırasında kullandığınız telefon numarası</small>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="btn-search" disabled={loading}>
                {loading ? 'Sorgulanıyor...' : 'Rezervasyonları Ara'}
              </button>
            </form>
          </>
        )}

        {/* Rezervasyon Listesi */}
        {bookings.length > 0 && !selectedBooking && (
          <div className="bookings-list">
            <div className="list-header">
              <h2>📋 Rezervasyonlarınız ({bookings.length})</h2>
              <button className="btn-new-search" onClick={resetSearch}>
                Yeni Arama
              </button>
            </div>

            {bookings.map((booking, index) => (
              <div 
                key={booking.reservation_id || index} 
                className="booking-list-item"
                onClick={() => setSelectedBooking(booking)}
              >
                <div className="booking-item-header">
                  <span className="reservation-no">{booking.reservation_id}</span>
                  <span 
                    className="status-badge-small" 
                    style={{ backgroundColor: getStatusText(booking.status).color }}
                  >
                    {getStatusText(booking.status).text}
                  </span>
                </div>
                <div className="booking-item-details">
                  <span>{getProductIcon(booking.product_type)} {getProductTypeLabel(booking.product_type).replace(/^[^\s]+\s/, '')}</span>
                  {booking.product_type === 'room' && booking.customer_info?.checkIn && (
                    <span>📅 {formatShortDate(booking.customer_info.checkIn)} - {formatShortDate(booking.customer_info.checkOut || '')}</span>
                  )}
                  {booking.product_type !== 'room' && booking.booking_date && (
                    <span>📅 {formatShortDate(booking.booking_date)}</span>
                  )}
                  <span>💰 {booking.total_price?.toLocaleString('tr-TR')} TL</span>
                </div>
                <div className="view-details">Detayları Gör →</div>
              </div>
            ))}
          </div>
        )}

        {/* Rezervasyon Detayı */}
        {selectedBooking && (
          <div className="booking-result">
            <div className="result-header">
              <button 
                className="btn-back-to-list"
                onClick={() => setSelectedBooking(null)}
              >
                ← Listeye Dön
              </button>
              <button className="btn-new-search" onClick={resetSearch}>
                Yeni Arama
              </button>
            </div>

            <div className="booking-card">
              <div className="product-type-badge" style={{ 
                background: 'linear-gradient(135deg, #2c3e50, #34495e)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: 600,
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                {getProductTypeLabel(selectedBooking.product_type)}
              </div>

              <div className="reservation-number">
                <span className="label">Rezervasyon No</span>
                <span className="value">{selectedBooking.reservation_id}</span>
              </div>

              <div className="status-badge" style={{ backgroundColor: getStatusText(selectedBooking.status).color }}>
                {getStatusText(selectedBooking.status).text}
              </div>

              <div className="details-section">
                <h3>📋 Rezervasyon Detayları</h3>
                
                <div className="details-grid">
                  {/* Oda Rezervasyonu İçin */}
                  {selectedBooking.product_type === 'room' && (
                    <>
                      {selectedBooking.items?.roomTypeName && (
                        <div className="detail-item">
                          <span className="icon">🏨</span>
                          <span className="label">Oda Tipi</span>
                          <span className="value">{selectedBooking.items.roomTypeName}</span>
                        </div>
                      )}
                      {selectedBooking.customer_info?.checkIn && (
                        <div className="detail-item">
                          <span className="icon">📅</span>
                          <span className="label">Giriş Tarihi</span>
                          <span className="value">{formatDate(selectedBooking.customer_info.checkIn)}</span>
                        </div>
                      )}
                      {selectedBooking.customer_info?.checkOut && (
                        <div className="detail-item">
                          <span className="icon">📅</span>
                          <span className="label">Çıkış Tarihi</span>
                          <span className="value">{formatDate(selectedBooking.customer_info.checkOut)}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="icon">🌙</span>
                        <span className="label">Konaklama</span>
                        <span className="value">{calculateNights(selectedBooking)} Gece</span>
                      </div>
                      {selectedBooking.customer_info?.adults && (
                        <div className="detail-item">
                          <span className="icon">👥</span>
                          <span className="label">Misafir</span>
                          <span className="value">
                            {selectedBooking.customer_info.adults} Yetişkin
                            {selectedBooking.customer_info.children ? `, ${selectedBooking.customer_info.children} Çocuk` : ''}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Şezlong Kiralama İçin */}
                  {selectedBooking.product_type === 'sunbed' && (
                    <>
                      <div className="detail-item">
                        <span className="icon">📅</span>
                        <span className="label">Tarih</span>
                        <span className="value">{formatDate(selectedBooking.booking_date)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="icon">🏖️</span>
                        <span className="label">Bölge Sayısı</span>
                        <span className="value">{selectedBooking.items?.zoneCount || selectedBooking.customer_info?.zoneDetails?.length || selectedBooking.items?.zones?.length || selectedBooking.items?.sunbedCount || selectedBooking.items?.sunbeds?.length || 1} bölge</span>
                      </div>
                      <div className="detail-item">
                        <span className="icon">👥</span>
                        <span className="label">Toplam Kişi</span>
                        <span className="value">
                          {selectedBooking.items?.totalPersons ||
                           (selectedBooking.customer_info?.zoneDetails || selectedBooking.items?.zones || []).reduce((s: number, z: any) => s + (z.person_count || 2), 0) ||
                           selectedBooking.items?.sunbeds?.length || 1} kişi
                        </span>
                      </div>
                      {selectedBooking.customer_info?.arrivalTime && (
                        <div className="detail-item">
                          <span className="icon">⏰</span>
                          <span className="label">Tahmini Varış</span>
                          <span className="value">{selectedBooking.customer_info.arrivalTime}</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Şezlong Bölge Detayları (yeni sistem) */}
                  {selectedBooking.product_type === 'sunbed' && (selectedBooking.customer_info?.zoneDetails || selectedBooking.items?.zones) && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#e8f4fd',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: '#2c3e50', fontSize: '0.95rem' }}>🏖️ Rezerve Edilen Bölgeler</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {(selectedBooking.customer_info?.zoneDetails || selectedBooking.items?.zones || []).map((z: any, index: number) => (
                            <div key={index} style={{
                              padding: '0.4rem 0.8rem',
                              backgroundColor: '#3498db',
                              color: 'white',
                              borderRadius: '15px',
                              fontSize: '0.85rem',
                              fontWeight: 500
                            }}>
                              {z.label ? z.label : `Bölge ${z.zone_id}`}
                              <span style={{ marginLeft: '0.4rem', opacity: 0.9 }}>👤 {z.person_count || 2} kişi</span>
                              {z.price ? <span style={{ marginLeft: '0.4rem', opacity: 0.9 }}>₺{z.price}</span> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Şezlong Detayları (eski sistem fallback) */}
                  {selectedBooking.product_type === 'sunbed' && !selectedBooking.customer_info?.zoneDetails && !selectedBooking.items?.zones && selectedBooking.items?.sunbeds && selectedBooking.items.sunbeds.length > 0 && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#e8f4fd',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: '#2c3e50', fontSize: '0.95rem' }}>🏖️ Rezerve Edilen Şezlonglar</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {selectedBooking.items.sunbeds.map((sunbed: any, index: number) => (
                            <div key={index} style={{
                              padding: '0.4rem 0.8rem',
                              backgroundColor: '#3498db',
                              color: 'white',
                              borderRadius: '15px',
                              fontSize: '0.85rem',
                              fontWeight: 500
                            }}>
                              Sıra {sunbed.row} - No: {sunbed.number}
                              <span style={{ marginLeft: '0.4rem', opacity: 0.9 }}>₺{sunbed.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Kahvaltı İçin */}
                  {selectedBooking.product_type === 'breakfast' && (
                    <>
                      <div className="detail-item">
                        <span className="icon">📅</span>
                        <span className="label">Tarih</span>
                        <span className="value">{formatDate(selectedBooking.booking_date)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="icon">👥</span>
                        <span className="label">Kişi Sayısı</span>
                        <span className="value">{selectedBooking.items?.guestCount || selectedBooking.items?.totalPersons || selectedBooking.customer_info?.guests || 1} kişi</span>
                      </div>
                      {selectedBooking.customer_info?.arrivalTime && (
                        <div className="detail-item">
                          <span className="icon">⏰</span>
                          <span className="label">Geliş Saati</span>
                          <span className="value">{selectedBooking.customer_info.arrivalTime}</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Akşam Yemeği İçin */}
                  {selectedBooking.product_type === 'dinner' && (
                    <>
                      <div className="detail-item">
                        <span className="icon">📅</span>
                        <span className="label">Tarih</span>
                        <span className="value">{formatDate(selectedBooking.booking_date)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="icon">👥</span>
                        <span className="label">Kişi Sayısı</span>
                        <span className="value">{selectedBooking.items?.guestCount || selectedBooking.items?.totalPersons || selectedBooking.customer_info?.guests || 1} kişi</span>
                      </div>
                      {selectedBooking.customer_info?.arrivalTime && (
                        <div className="detail-item">
                          <span className="icon">⏰</span>
                          <span className="label">Rezervasyon Saati</span>
                          <span className="value">{selectedBooking.customer_info.arrivalTime}</span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="detail-item total">
                    <span className="icon">💳</span>
                    <span className="label">Toplam Tutar</span>
                    <span className="value price">{selectedBooking.total_price?.toLocaleString('tr-TR')} TL</span>
                  </div>
                </div>
              </div>

              <div className="guest-section">
                <h3>👤 Misafir Bilgileri</h3>
                <p><strong>{selectedBooking.customer_name}</strong></p>
                <p>{selectedBooking.customer_email}</p>
                {(selectedBooking.customer_phone || selectedBooking.customer_info?.phone) && 
                  <p>📱 {selectedBooking.customer_phone || selectedBooking.customer_info?.phone}</p>
                }
              </div>

              {/* Oda için check-in bilgileri */}
              {selectedBooking.product_type === 'room' && (
                <div className="checkin-section">
                  <h3>🏨 Check-in Bilgileri</h3>
                  <p><strong>Giriş Saati:</strong> 14:00'den itibaren</p>
                  <p><strong>Çıkış Saati:</strong> 11:00'ye kadar</p>
                </div>
              )}

              {/* Şezlong için bilgiler */}
              {selectedBooking.product_type === 'sunbed' && (
                <div className="checkin-section">
                  <h3>🏖️ Şezlong Bilgileri</h3>
                  <p><strong>Kullanım Saatleri:</strong> 09:00 - 19:00</p>
                  <p><strong>Konum:</strong> Plaj Alanı</p>
                  <p>📍 Şezlong numaralarınız resepsiyondan öğrenilebilir.</p>
                </div>
              )}

              {/* Kahvaltı için bilgiler */}
              {selectedBooking.product_type === 'breakfast' && (
                <div className="checkin-section">
                  <h3>🍳 Kahvaltı Bilgileri</h3>
                  <p><strong>Kahvaltı Saatleri:</strong> 08:00 - 11:00</p>
                  <p><strong>Konum:</strong> Havuz Başı / Teras</p>
                  <p>📍 Geldiğinizde resepsiyona rezervasyon numaranızı söyleyiniz.</p>
                </div>
              )}

              {/* Akşam yemeği için bilgiler */}
              {selectedBooking.product_type === 'dinner' && (
                <div className="checkin-section">
                  <h3>🍽️ Yemek Bilgileri</h3>
                  <p><strong>Akşam Yemeği Saatleri:</strong> 19:00 - 22:00</p>
                  <p><strong>Konum:</strong> Ana Restoran</p>
                  <p>📍 Geldiğinizde resepsiyona rezervasyon numaranızı söyleyiniz.</p>
                </div>
              )}

              <div className="contact-section">
                <p>📧 Sorularınız için: <a href="mailto:info@villapolikne.com">info@villapolikne.com</a></p>
                <p>📱 Telefon: <a href="tel:+905534062844">+90 553 406 28 44</a></p>
              </div>
            </div>
          </div>
        )}

        {searched && bookings.length === 0 && !loading && !error && (
          <div className="no-result">
            <p>Girdiğiniz bilgilerle eşleşen rezervasyon bulunamadı.</p>
            <button className="btn-new-search" onClick={resetSearch}>
              Tekrar Dene
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationLookup;
